import { useEffect, useMemo, useState } from 'react';
import * as api from '../services/api';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmNoteModal } from '../components/ConfirmNoteModal';
import { FilterBar } from '../components/FilterBar';
import type { RoleChangeRequest, RoleChangeRequestStatus } from '../types/auth';

const STATUS_OPTIONS: Array<RoleChangeRequestStatus | ''> = [
  '',
  'pending',
  'approved',
  'rejected',
  'cancelled'
];

export function RoleChangeRequestsPage() {
  const [requests, setRequests] = useState<RoleChangeRequest[]>([]);
  const [status, setStatus] = useState<RoleChangeRequestStatus | ''>('pending');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [noteTarget, setNoteTarget] = useState<{
    requestId: string;
    decision: 'approve' | 'reject';
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.listRoleChangeRequests(status);
        if (!cancelled) setRequests(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load role change requests');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((request) => {
      const haystack = [
        request.targetUserId,
        request.requesterUserId,
        request.schoolId ?? '',
        request.currentRole,
        request.requestedRole,
        request.reason ?? '',
        request.reviewNote ?? ''
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [requests, search]);

  async function confirmReview(note: string) {
    if (!noteTarget) return;
    const { requestId, decision } = noteTarget;
    setBusyId(requestId);
    setError(null);
    try {
      await api.reviewRoleChangeRequest(requestId, decision, note || undefined);
      const refreshed = await api.listRoleChangeRequests(status);
      setRequests(refreshed);
      setNoteTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${decision} request`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="admin-panel">
      <div className="panel-heading">
        <div>
          <h2>Role change requests</h2>
          <p>Review identity-level role changes. SUPER_ADMIN cannot be approved via this flow.</p>
        </div>
        <span className="panel-meta">{filtered.length} shown</span>
      </div>

      <FilterBar
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(searchInput.trim());
        }}
      >
        <label>
          Search
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="User id, role, reason"
          />
        </label>
        <label>
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as RoleChangeRequestStatus | '')}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option || 'all'} value={option}>
                {option || 'All'}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="primary-btn">
          Apply
        </button>
      </FilterBar>

      {error ? <p className="form-error" role="alert">{error}</p> : null}
      {loading ? <p className="muted">Loading requests…</p> : null}
      {!loading && filtered.length === 0 ? <p className="muted">No role change requests.</p> : null}

      {filtered.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Target</th>
                <th>Current → Requested</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((request) => (
                <tr key={request.id}>
                  <td>
                    <code>{request.targetUserId}</code>
                  </td>
                  <td>
                    {request.currentRole} → {request.requestedRole}
                  </td>
                  <td>
                    <StatusBadge status={request.status} />
                  </td>
                  <td>{request.reason || '—'}</td>
                  <td>{request.createdAt ? new Date(request.createdAt).toLocaleString() : '—'}</td>
                  <td>
                    {request.status === 'pending' ? (
                      <div className="action-row">
                        <button
                          type="button"
                          className="ghost-btn compact"
                          disabled={busyId === request.id}
                          onClick={() =>
                            setNoteTarget({ requestId: request.id, decision: 'approve' })
                          }
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="ghost-btn compact"
                          disabled={busyId === request.id}
                          onClick={() =>
                            setNoteTarget({ requestId: request.id, decision: 'reject' })
                          }
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <ConfirmNoteModal
        open={Boolean(noteTarget)}
        title={noteTarget ? `${noteTarget.decision} request` : 'Confirm'}
        message={
          noteTarget
            ? `Are you sure you want to ${noteTarget.decision} this role change request?`
            : ''
        }
        confirmLabel={noteTarget?.decision === 'approve' ? 'Approve' : 'Reject'}
        noteLabel="Optional review note"
        busy={Boolean(noteTarget && busyId === noteTarget.requestId)}
        onCancel={() => setNoteTarget(null)}
        onConfirm={confirmReview}
      />
    </section>
  );
}
